import {
  canAccessClientes,
  canAccessTemplates,
  canAccessUsuarios,
  canAccessSupervisao,
} from '@/lib/Permissions';

/**
 * Catálogo único de itens de navegação (Home, sidebar e cabeçalhos de página).
 * route: nome da rota Vue Router
 * action: evento global emitido via $root (App.vue)
 */
export const APP_NAV_ITEMS = [
  {
    id: 'home',
    label: 'Home',
    icon: 'fas fa-home',
    gradient: 'linear-gradient(135deg, #5e5ce6, #007aff)',
    route: 'home',
    canAccess: () => true,
  },
  {
    id: 'pesquisa',
    label: 'Pesquisa',
    icon: 'fas fa-search',
    gradient: 'linear-gradient(135deg, #5856d6, #af52de)',
    action: 'openQuickSearch',
    canAccess: () => true,
  },
  {
    id: 'clientes',
    label: 'Clientes',
    icon: 'fas fa-user-friends',
    gradient: 'linear-gradient(135deg, #007aff, #5ac8fa)',
    route: 'customers',
    canAccess: canAccessClientes,
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: 'fas fa-hammer',
    gradient: 'linear-gradient(135deg, #ff9500, #ffcc00)',
    route: 'templates',
    canAccess: canAccessTemplates,
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: 'fas fa-users-cog',
    gradient: 'linear-gradient(135deg, #8e8e93, #636366)',
    route: 'settings',
    canAccess: () => true,
  },
  {
    id: 'landing-pages',
    label: 'Landing Pages',
    icon: 'fas fa-globe',
    gradient: 'linear-gradient(135deg, #34c759, #30d158)',
    route: 'landingPages',
    canAccess: () => true,
  },
  {
    id: 'upload',
    label: 'Upload',
    icon: 'fas fa-upload',
    gradient: 'linear-gradient(135deg, #32ade6, #007aff)',
    route: 'uploadFile',
    canAccess: () => true,
  },
  {
    id: 'dump',
    label: 'Dump',
    icon: 'fas fa-copy',
    gradient: 'linear-gradient(135deg, #5e5ce6, #5856d6)',
    route: 'dump',
    canAccess: () => true,
  },
  {
    id: 'limpeza',
    label: 'Limpeza',
    icon: 'fas fa-broom',
    gradient: 'linear-gradient(135deg, #ff2d55, #ff6482)',
    route: 'limpeza',
    canAccess: () => true,
  },
  {
    id: 'inicializar-indices',
    label: 'Inicializar índices',
    icon: 'fas fa-database',
    gradient: 'linear-gradient(135deg, #64d2ff, #0a84ff)',
    action: 'openInicializarSistema',
    canAccess: canAccessClientes,
  },
  {
    id: 'usuarios',
    label: 'Usuários',
    icon: 'fas fa-address-card',
    gradient: 'linear-gradient(135deg, #bf5af2, #af52de)',
    route: 'usuario',
    canAccess: canAccessUsuarios,
  },
  {
    id: 'oauth-apps',
    label: 'Apps e Atalhos',
    icon: 'fas fa-key',
    gradient: 'linear-gradient(135deg, #ffd60a, #ff9f0a)',
    route: 'oauthApps',
    canAccess: canAccessUsuarios,
  },
  {
    id: 'supervisao',
    label: 'Supervisão',
    icon: 'fas fa-user-shield',
    gradient: 'linear-gradient(135deg, #30b0c7, #007aff)',
    route: 'supervisao',
    canAccess: (permissions, options = {}) =>
      canAccessSupervisao(permissions, options.supervisionados || []),
  },
  {
    id: 'custos-variaveis',
    label: 'Custos Variáveis',
    icon: 'fas fa-chart-line',
    gradient: 'linear-gradient(135deg, #30b0c7, #34c759)',
    route: 'custosVariaveis',
    canAccess: () => true,
  },
];

const ROUTE_TO_NAV_ID = {
  intro: 'home',
  home: 'home',
  customers: 'clientes',
  templates: 'templates',
  templateReorganizar: 'templates',
  templateEditar: 'templates',
  templateEditarEntradaEmMassa: 'templates',
  settings: 'configuracoes',
  configuracaoEditar: 'configuracoes',
  configuracaoHistorico: 'configuracoes',
  landingPages: 'landing-pages',
  uploadFile: 'upload',
  dump: 'dump',
  dumpView: 'dump',
  dumpsHistorico: 'dump',
  limpeza: 'limpeza',
  limpezaView: 'limpeza',
  limpezasHistorico: 'limpeza',
  usuario: 'usuarios',
  usuarioNovo: 'usuarios',
  usuarioEditar: 'usuarios',
  usuarioVisualizar: 'usuarios',
  oauthApps: 'oauth-apps',
  oauthAppNovo: 'oauth-apps',
  oauthAppEditar: 'oauth-apps',
  oauthIntegracaoTutorial: 'oauth-apps',
  oauthIntegracaoTutorialTipo: 'oauth-apps',
  supervisao: 'supervisao',
  custosVariaveis: 'custos-variaveis',
};

const navItemsById = APP_NAV_ITEMS.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {});

const navItemsByRoute = APP_NAV_ITEMS.reduce((acc, item) => {
  if (item.route) {
    acc[item.route] = item;
  }
  return acc;
}, {});

export function getNavItemById(navId) {
  return navItemsById[navId] || null;
}

export function getNavItemForRoute(routeName) {
  if (!routeName) {
    return null;
  }

  const navId = ROUTE_TO_NAV_ID[routeName];
  if (navId) {
    return getNavItemById(navId);
  }

  return navItemsByRoute[routeName] || null;
}

export function getHomeShortcuts(userPermissions, options = {}) {
  const permissions = userPermissions || {};
  return APP_NAV_ITEMS.filter((item) => {
    if (item.id === 'home') {
      return false;
    }
    return item.canAccess(permissions, options);
  });
}
